import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton/ConfirmButton";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import { DEFAULT_INITIAL_PAGINATION_DATA } from "@saleor/config";
import { customerPath } from "@saleor/customers/urls";
import useDownloadReport from "@saleor/hooks/useDownloadReport";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import Moment from "moment";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ReportsUrl } from "../../../overview/urls";
import { ageCalculator, InvalidThreeMonth } from "../../../utils/misc";
import { reportsDownload } from "../../../utils/utils";
import CustomerInfoListPage from "../../components/CustomerInfoListPage";
import { useCustomerInfoListQuery } from "../../queries";
import {
  CustomerInfoListUrl,
  CustomerInfoListUrlDialog,
  CustomerInfoUrlQueryParams
} from "../../urls";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
interface CustomerInfoListProp {
  params: CustomerInfoUrlQueryParams;
}

const customerInfoList: React.FC<CustomerInfoListProp> = ({ params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const shop = useShop();
  const notify = useNotifier();

  const intl = useIntl();
  const dateNow = Moment(new Date()).format("YYYYMMDDHHmmss");
  const sheetName = "Customer Information";
  const fileName = `${reportsDownload.customerInfo.filename}-${dateNow}.xlsx`;
  const currencySymbol = maybe(() => shop.defaultCurrency, "PHP");
  const [loadData, setLoadData] = useState(true);
  const [downloadPagination, setDownloadPagination] = useState("");
  const [downloadStatus, setDownloadStatus] = useState<
    ConfirmButtonTransitionState
  >("default");
  const { updateListSettings, settings } = useListSettings(
    ListViews.REPORTS_LIST
  );

  React.useEffect(
    () =>
      navigate(
        CustomerInfoListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  // Todo - <Marco>: Explore on how to fix from BE
  React.useEffect(() => {
    if (params.query !== null) {
      params.after = null;
      params.before = null;
    }
  }, [params]);

  const paginationState = createPaginationState(settings.rowNumber, params);

  React.useEffect(() => {
    if (InvalidThreeMonth(params.joinedFrom, params.joinedTo)) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Unable to filter the report data. Please limit the date range to 3 months."
        })
      });
      navigate(CustomerInfoListUrl());
    }
  }, [params.joinedTo]);

  const previewVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params]
  );

  const reportVariables = React.useMemo(
    () => ({
      after: downloadPagination,
      filter: getFilterVariables(params),
      first: 1000
    }),
    [params]
  );

  const { data: previewData } = useCustomerInfoListQuery({
    displayLoader: true,
    skip: Object.keys(params).length === 0,
    variables: previewVariables
  });

  const { data: reportData, loading: reportLoading } = useCustomerInfoListQuery(
    {
      displayLoader: true,
      skip: loadData,
      variables: reportVariables
    }
  );

  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    createUrl: CustomerInfoListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    CustomerInfoListUrlDialog,
    CustomerInfoUrlQueryParams
  >(navigate, CustomerInfoListUrl, params);

  const handleTabChange = (tab: number) => {
    navigate(
      CustomerInfoListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    navigate(CustomerInfoListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    previewData?.customerInfo?.pageInfo,
    paginationState,
    params
  );

  const selectReportData = reportData => {
    const selectedData = reportData?.map(data => {
      delete data.__typename;
      delete data.id;
      data.age = data?.date_of_birth ? ageCalculator(data?.date_of_birth) : "";
      data.date_joined = data?.date_joined
        ? Moment.tz(new Date(data?.date_joined), "Asia/Manila").format(
            "YYYY-MM-DD HH:mm:ss"
          )
        : null;
      data.date_of_birth = data?.date_of_birth
        ? Moment.tz(new Date(data?.date_of_birth), "Asia/Manila").format(
            "YYYY-MM-DD"
          )
        : null;
      return data;
    });
    return selectedData;
  };

  const shownCustomers = previewData?.customerInfo?.edges.map(
    edge => edge.node
  );
  const allCustomers = reportData?.customerInfo?.edges.map(edge => edge.node);
  const [downloadData, setDownloadData] = useState([]);
  const nextPage = reportData?.customerInfo?.pageInfo;

  React.useEffect(() => {
    if (reportData && !reportLoading) {
      setDownloadData(current => [...current, allCustomers]);
      setDownloadPagination(nextPage?.endCursor);
      if (!nextPage?.hasNextPage && nextPage?.endCursor === null) {
        const selectedReportData = selectReportData(downloadData?.flat());
        useDownloadReport(
          selectedReportData,
          reportsDownload.customerInfo.fields,
          sheetName,
          fileName
        );
        setDownloadStatus("success");
        setDownloadPagination("");
        setLoadData(true);
        setDownloadData([]);
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Customer Information successfully downloaded."
          })
        });
        closeModal();
      }
    }
  }, [reportData]);

  const cancelDownload = () => {
    setLoadData(true);
    setDownloadData([]);
    setDownloadStatus("default");
    setDownloadPagination("");
    closeModal();
  };

  return (
    <>
      <CustomerInfoListPage
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        filterOpts={getFilterOpts(params)}
        initialSearch={params.query || ""}
        onSearchChange={handleSearchChange}
        onFilterChange={changeFilters}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        tabs={tabs.map(tab => tab.name)}
        customers={shownCustomers}
        disabled={reportLoading}
        pageInfo={pageInfo}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        settings={settings}
        onBack={() => navigate(ReportsUrl)}
        download={() => openModal("download")}
        onRowClick={id => () => window.open(customerPath(id))}
      />
      <ActionDialog
        open={params.action === "download"}
        onClose={cancelDownload}
        confirmButtonState={downloadStatus}
        onConfirm={() => {
          setLoadData(false);
          setDownloadStatus("loading");
          setDownloadData([]);
          setDownloadPagination("");
        }}
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Download Customer Information",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="You are about to download a customer information with {total} rows. Do you want to continue?"
            values={{
              total: <strong>{previewData?.customerInfo?.totalCount}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>

      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={maybe(() => tabs[currentTab - 1].name, "...")}
      />
    </>
  );
};

export default customerInfoList;
